import _ from "lodash";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import Select from "react-select";
import { Col, Input, Label, Row } from "reactstrap";
import { constants } from "../utils";

// props.locked: true while OrderPlacement's submit is in flight, so nothing can be edited after the click and then
// silently dropped from the order that is already on its way. The address fields' own readOnly rule is unchanged.
export default function OrderDetails(props) {
  const [activeBillAddress, setActiveBillAddress] = useState();
  const [activeShipAddress, setActiveShipAddress] = useState();
  const [readingShipAddress, setReadingShipAddress] = useState(true);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateList, setStateList] = useState();

  useEffect(() => {
    loadBillAddress(props.billToAddressCode);
    loadShipAddress(props.shipToAddressCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  function handleCountryChange(e) {
    if (e.value === "US") {
      setShowStateDropdown(true);
      setStateList(constants.STATES);
    } else if (e.value === "CA" || e.value === "CANADA") {
      setShowStateDropdown(true);
      setStateList(constants.PROVINCES);
    } else {
      setShowStateDropdown(false);
      setStateList(null);
    }
    props.updateOrderShipTo(props.siteNumber, "country", e.label);
    props.updateOrderShipTo(props.siteNumber, "countryCode", e.value);
  }

  function loadBillAddress(addressCode) {
    const billAddress = _.find(props.addresses, (address) => address.addressCode === addressCode);
    setActiveBillAddress(billAddress);
  }

  function loadShipAddress(addressCode) {
    if (addressCode === "-- New Address --") {
      setShowStateDropdown(false);
      setReadingShipAddress(false);
      setActiveShipAddress({ addressCode: addressCode });
    } else {
      setReadingShipAddress(true);
      const shipAddress = _.find(
        props.addresses,
        (address) => address.addressCode === addressCode
      );
      setActiveShipAddress(shipAddress);
      if (_.includes(["US", "CA", "CANADA"], shipAddress.countryCode)) {
        setShowStateDropdown(true);
        if (shipAddress.countryCode === "US") {
          setStateList(constants.STATES);
        } else {
          setStateList(constants.PROVINCES);
        }
      } else {
        setShowStateDropdown(false);
      }
    }
  }

  if (
    !activeBillAddress ||
    !activeShipAddress ||
    (props.reuseDetails && props.siteNumber !== 0)
  ) {
    return null;
  }

  return (
    <>
      <h4 className="orderDetailHeader">Payment Information</h4>
      <Row className="orderDetailForm">
        <Col xs={1}>
          <div
            children="Purchase Order:"
            className="fw-bold float-end black orderDetailsTitles"
          />
        </Col>
        <Col xs={4}>
          <Input
            id="PurchaseOrder"
            value={props.poNumber}
            onChange={(e) => props.updateOrderDetails(props.siteNumber, "poNumber", e.target.value)}
            disabled={props.locked}
          />
        </Col>
      </Row>
      <hr />
      <Row>
        <Col>
          <h4 className="orderDetailHeader">Bill To Address:</h4>
          <Row>
            <Label children="Address:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Select
                classNamePrefix="serviceThreadSelect"
                id="addressType"
                value={{
                  value: activeBillAddress.addressCode,
                  label: activeBillAddress.ddlText,
                }}
                options={_.map(props.addresses, (adr) => {
                  return { value: adr.addressCode, label: adr.ddlText };
                })}
                onChange={(opt) => props.updateOrderDetails(props.siteNumber, "billToCode", opt.value)}
                isDisabled={props.locked}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Country:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.country}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="Company:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.address1}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="Address 1:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.address2}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="Address 2:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.address3}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="City:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.city}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="State:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.state}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="Zip Code:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.zip}
                readOnly
              />
            </Col>
          </Row>
          <Row>
            <Label children="Contact:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                className="readonly-input"
                value={activeBillAddress.contact}
                readOnly
              />
            </Col>
          </Row>
        </Col>

        {/* /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////// */}

        <Col xs="6">
          <h4 className="orderDetailHeader">Ship To Address:</h4>
          <Row>
            <Label children="Address:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Select
                classNamePrefix="serviceThreadSelect"
                id="addressType"
                value={{
                  value: activeShipAddress.addressCode,
                  label: activeShipAddress.ddlText,
                }}
                options={_.concat(
                  { value: "-- New Address --", label: "-- New Address --" },
                  _.map(props.addresses, (adr) => {
                    return { value: adr.addressCode, label: adr.ddlText };
                  })
                )}
                onChange={(opt) => props.updateOrderDetails(props.siteNumber, "shipToCode", opt.value)}
                isDisabled={props.locked}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Country:" xs={2} className="fw-bold" />
            <Col xs={10}>
              {readingShipAddress ? (
                <Input
                  className="readonly-input"
                  value={activeShipAddress.country}
                  readOnly={readingShipAddress}
                />
              ) : (
                <Select
                  classNamePrefix="serviceThreadSelect"
                  id="country"
                  options={props.countryList}
                  value={
                    props.newAddressInfo.countryCode
                      ? _.find(props.countryList, (c) => c.value === props.newAddressInfo.countryCode)
                      : ""
                  }
                  onChange={handleCountryChange}
                  isDisabled={props.locked}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Label children="Company:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="Company"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.address1) || ""
                    : props.newAddressInfo.address1
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "address1", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Address 1:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="saddress2"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.address2) || ""
                    : props.newAddressInfo.address2
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "address2", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Address 2:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="saddress3"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.address3) || ""
                    : props.newAddressInfo.address3
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "address3", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="City:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="city"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.city) || ""
                    : props.newAddressInfo.city
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "city", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="State:" xs={2} className="fw-bold" />
            <Col xs={10}>
              {readingShipAddress ? (
                <Input
                  value={activeShipAddress.state}
                  className="readonly-input"
                  readOnly
                />
              ) : showStateDropdown ? (
                <Select
                  classNamePrefix="serviceThreadSelect"
                  id="state"
                  options={stateList}
                  value={
                    activeShipAddress.state && {
                      value: activeShipAddress.state,
                      label: activeShipAddress.state,
                    }
                  }
                  onChange={(e) => props.updateOrderShipTo(props.siteNumber, "state", e.value)}
                  isDisabled={props.locked}
                  isClearable
                />
              ) : (
                <Input
                  id="state"
                  value={
                    readingShipAddress
                      ? activeShipAddress.state
                      : props.newAddressInfo.state
                  }
                  onChange={(e) => props.updateOrderShipTo(props.siteNumber, "state", e.target.value)}
                  disabled={props.locked}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Label children="Zip Code:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="zip"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.zip) || ""
                    : props.newAddressInfo.zip
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "zip", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Contact:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <Input
                id="shipContact"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.contact) || ""
                    : props.newAddressInfo.contact
                }
                onChange={(e) => props.updateOrderShipTo(props.siteNumber, "contact", e.target.value)}
                disabled={props.locked}
                className={readingShipAddress ? "readonly-input" : ""}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Phone:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <NumberFormat
                id="phone"
                className={`form-control${readingShipAddress ? " readonly-input" : ""}`}
                format="(###) ###-####"
                mask="_"
                maxLength="15"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.phone) || ""
                    : props.newAddressInfo.phone
                }
                disabled={props.locked}
                onValueChange={(values) => {
                  props.updateOrderShipTo(props.siteNumber, "phone", values.value);
                }}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
          <Row>
            <Label children="Fax:" xs={2} className="fw-bold" />
            <Col xs={10}>
              <NumberFormat
                id="fax"
                className={`form-control${readingShipAddress ? " readonly-input" : ""}`}
                format="(###) ###-####"
                mask="_"
                maxLength="15"
                value={
                  readingShipAddress
                    ? (activeShipAddress && activeShipAddress.fax) || ""
                    : props.newAddressInfo.fax
                }
                disabled={props.locked}
                onValueChange={(values) => {
                  props.updateOrderShipTo(props.siteNumber, "fax", values.value);
                }}
                readOnly={readingShipAddress}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs="4">
          <Label for="exampleText">Order Note:</Label>
          <Input
            className="serviceThreadTextArea"
            type="textarea"
            value={props.orderNote}
            onChange={(e) => props.updateOrderDetails(props.siteNumber, "orderNote", e.target.value)}
            disabled={props.locked}
          />
        </Col>
        <Col xs="4">
          <Label for="exampleText">Follow Up Note:</Label>
          <Input
            className="serviceThreadTextArea"
            type="textarea"
            value={props.followUpNote}
            onChange={(e) => props.updateOrderDetails(props.siteNumber, "followUpNote", e.target.value)}
            disabled={props.locked}
          />
        </Col>
        <Col xs={1} style={{ display: "flex", alignItems: "center" }}>
          <div
            children="Follow Up Date:"
            className="fw-bold float-end black orderDetailsTitles"
          />
        </Col>
        <Col xs={3} style={{ display: "flex", alignItems: "center" }}>
          <Input
            type="Date"
            name="Date"
            value={props.followUpDate}
            onChange={(e) => props.updateOrderDetails(props.siteNumber, "followUpDate", e.target.value)}
            disabled={props.locked}
          />
        </Col>
      </Row>
    </>
  );
}
